import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ShieldAlert, X } from 'lucide-react'
import { getWarrantyCoverage, type WarrantyCoverage } from '@/services/dashboardApi'
import { buildWarrantyAlertCopy, shouldShowWarrantyAlert } from './warrantyAlertCopy'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'

const PRODUCTS_ROUTE = '/app/products'
const GENERAL_POLICY_ROUTE = '/app/bot-settings'

interface WarrantyCoverageAlertViewProps {
  coverage: WarrantyCoverage
  onDismiss: () => void
}

export function WarrantyCoverageAlertView({ coverage, onDismiss }: WarrantyCoverageAlertViewProps) {
  const { title, body } = buildWarrantyAlertCopy(coverage)

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
    >
      <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-sm text-amber-800">{body}</p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold">
          <Link to={PRODUCTS_ROUTE} className="underline hover:text-amber-950">
            Configurar garantías de productos
          </Link>
          <Link to={GENERAL_POLICY_ROUTE} className="underline hover:text-amber-950">
            {coverage.hasGeneralPolicy ? 'Revisar política general' : 'Definir política general'}
          </Link>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar aviso de garantías"
        className="rounded-md p-1 text-amber-700 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}

/**
 * Aviso no bloqueante del dashboard. Si la consulta falla o está cargando no muestra nada:
 * es una sugerencia, nunca debe estorbar ni bloquear el resto del panel.
 */
export default function WarrantyCoverageAlert() {
  const tenantId = useAuthStore((s) => s.tenantId)
  const [dismissed, setDismissed] = useState(false)

  const { data } = useQuery<WarrantyCoverage>({
    queryKey: queryKeys.warrantyCoverage(tenantId),
    queryFn: getWarrantyCoverage,
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
    retry: false,
  })

  if (dismissed || !shouldShowWarrantyAlert(data)) return null

  return <WarrantyCoverageAlertView coverage={data} onDismiss={() => setDismissed(true)} />
}
